/**
 * Shared AI writing generation orchestration (PBI #1152).
 *
 * Hard invariant: e-card and mailed assistants must call generate/stream through
 * this hook (or {@link buildAiWritingGenerationRequest}) so empty-context Generate,
 * tone, and the `instructions` mapping contract cannot drift.
 *
 * - **ecard:** prefers SSE {@link streamGreeting}, falls back to {@link generateGreeting}.
 * - **mailed:** non-streaming {@link generateGreeting} only (stream is e-card-only server-side).
 */

import { useCallback, useRef, useState } from 'react';
import type { AxiosError } from 'axios';

import {
  generateGreeting,
  streamGreeting,
  type GenerateGreetingResponse,
  type GreetingStreamEvent,
} from '../services/greetingGenerationService';
import { GreetingGenerationModes } from '../constants/greetingGenerationModes';
import { UPGRADE_REQUIRED_EVENT } from '../contexts/UpgradeModalContext';
import { SUBSCRIPTION_EXPIRED_EVENT } from '../services/apiClient';
import {
  buildAiWritingGenerationRequest,
  type AiWritingMode,
  type AiWritingRunMode,
  type BuildAiWritingGenerationRequestArgs,
} from './buildAiWritingGenerationRequest';

export type { AiWritingMode, AiWritingRunMode };

/** Successful generation payload surfaced to the modal. */
export interface AiWritingGenerationSuccess {
  subjectLine: string;
  greetingBody: string;
  occasionHintWarnings: string[];
}

export interface UseAiWritingGenerationOptions {
  writingMode: AiWritingMode;
  contactId: number;
  eventTypeId: number;
  cardTemplateId?: number;
  eventDate?: string;
  maxBodyLength?: number;
  /** Invoked on HTTP 402 so the caller can register upgrade replay. */
  onPaymentRequired?: () => void;
}

export interface RunAiWritingGenerationArgs {
  runMode: AiWritingRunMode;
  tone?: string;
  /** Intent (mailed suggest) or rewrite instructions / chip (ecard rewrite). */
  instructions?: string;
  existingGreetingBody?: string;
  /**
   * When true (ecard), stream deltas into `onStreamDelta` before completing.
   * Mailed always ignores streaming.
   */
  preferStream?: boolean;
  onStreamDelta?: (partial: { subjectLine: string; greetingBody: string }) => void;
  onStreamError?: (message: string) => void;
}

/**
 * Parses a generate/stream failure into a user-facing message.
 */
export function formatAiWritingGenerationError(err: unknown): string {
  const axiosErr = err as AxiosError<{
    message?: string;
    Message?: string;
    title?: string;
    errors?: Record<string, string[]>;
  }>;
  const validationErrors = axiosErr?.response?.data?.errors;
  const validationMessage = validationErrors
    ? Object.entries(validationErrors)
        .flatMap(([field, messages]) => messages.map((m) => `${field}: ${m}`))
        .join(' ')
    : undefined;
  const status = axiosErr?.response?.status;
  if (status === 429) {
    return (
      axiosErr?.response?.data?.message
      ?? axiosErr?.response?.data?.Message
      ?? 'Monthly regeneration limit reached.'
    );
  }
  if (status === 422) {
    return (
      axiosErr?.response?.data?.message
      ?? axiosErr?.response?.data?.Message
      ?? 'Generation did not pass validation. Please try again.'
    );
  }
  return (
    axiosErr?.response?.data?.message
    ?? axiosErr?.response?.data?.Message
    ?? validationMessage
    ?? axiosErr?.response?.data?.title
    ?? 'AI generation failed. Please try again.'
  );
}

/**
 * Shared generate/rewrite runner for e-card and mailed AI assistants.
 */
export function useAiWritingGeneration(options: UseAiWritingGenerationOptions) {
  const {
    writingMode,
    contactId,
    eventTypeId,
    cardTemplateId,
    eventDate,
    maxBodyLength,
    onPaymentRequired,
  } = options;

  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [occasionHintWarnings, setOccasionHintWarnings] = useState<string[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const runGeneration = useCallback(
    async (args: RunAiWritingGenerationArgs): Promise<AiWritingGenerationSuccess | null> => {
      setError(null);
      setOccasionHintWarnings([]);
      setIsGenerating(true);

      const buildArgs: BuildAiWritingGenerationRequestArgs = {
        writingMode,
        runMode: args.runMode,
        contactId,
        eventTypeId,
        cardTemplateId,
        eventDate,
        tone: args.tone,
        instructions: args.instructions,
        existingGreetingBody: args.existingGreetingBody,
        maxBodyLength,
      };
      const request = buildAiWritingGenerationRequest(buildArgs);

      const useStream =
        writingMode === GreetingGenerationModes.ECard && args.preferStream !== false;

      try {
        if (useStream) {
          const controller = new AbortController();
          abortRef.current?.abort();
          abortRef.current = controller;

          const accumulator = { subjectLine: '', greetingBody: '' };
          let streamFailedMessage: string | null = null;
          let streamWarnings: string[] = [];

          try {
            await streamGreeting(
              request,
              (evt: GreetingStreamEvent) => {
                switch (evt.event) {
                  case 'delta':
                    if (evt.field === 'subjectLine') {
                      accumulator.subjectLine += evt.text;
                    } else if (evt.field === 'greetingBody') {
                      accumulator.greetingBody += evt.text;
                    }
                    args.onStreamDelta?.({ ...accumulator });
                    break;
                  case 'error':
                    streamFailedMessage = evt.message || 'Generation failed.';

                    // generate-stream answers HTTP 200 and an SSE error (#1483),
                    // so the axios 402 interceptor never opens Upgrade Your Plan.
                    // Dispatch the same events that interceptor uses for a 402.
                    if (evt.code === 'FEATURE_REQUIRED') {
                      onPaymentRequired?.();
                      window.dispatchEvent(new CustomEvent(SUBSCRIPTION_EXPIRED_EVENT));
                      window.dispatchEvent(
                        new CustomEvent(UPGRADE_REQUIRED_EVENT, {
                          detail: { reason: streamFailedMessage },
                        }),
                      );
                    }
                    args.onStreamError?.(streamFailedMessage);
                    break;
                  case 'done':
                    streamWarnings = Array.isArray(evt.occasionHintWarnings)
                      ? evt.occasionHintWarnings
                      : [];
                    setOccasionHintWarnings(streamWarnings);
                    break;
                  default:
                    break;
                }
              },
              controller.signal,
            );

            if (streamFailedMessage) {
              setError(streamFailedMessage);
              return null;
            }

            return {
              subjectLine: accumulator.subjectLine,
              greetingBody: accumulator.greetingBody,
              occasionHintWarnings: streamWarnings,
            };
          } catch (err) {
            if ((err as Error).name === 'AbortError') {
              return null;
            }
            const status =
              (err as { status?: number; response?: { status?: number } }).status
              ?? (err as { response?: { status?: number } }).response?.status;
            if (status === 402) {
              onPaymentRequired?.();
              return null;
            }

            // Streaming failed — fall back to non-streaming generate.
            const fallback = await generateGreeting(request);
            setOccasionHintWarnings(fallback.occasionHintWarnings ?? []);
            return {
              subjectLine: fallback.subjectLine,
              greetingBody: fallback.greetingBody,
              occasionHintWarnings: fallback.occasionHintWarnings ?? [],
            };
          }
        }

        const response: GenerateGreetingResponse = await generateGreeting(request);
        setOccasionHintWarnings(response.occasionHintWarnings ?? []);
        return {
          subjectLine: response.subjectLine,
          greetingBody: response.greetingBody,
          occasionHintWarnings: response.occasionHintWarnings ?? [],
        };
      } catch (err) {
        const status = (err as AxiosError).response?.status;
        if (status === 402) {
          onPaymentRequired?.();
          return null;
        }
        setError(formatAiWritingGenerationError(err));
        return null;
      } finally {
        setIsGenerating(false);
        abortRef.current = null;
      }
    },
    [
      writingMode,
      contactId,
      eventTypeId,
      cardTemplateId,
      eventDate,
      maxBodyLength,
      onPaymentRequired,
    ],
  );

  return {
    isGenerating,
    error,
    setError,
    occasionHintWarnings,
    setOccasionHintWarnings,
    runGeneration,
    cancel,
  };
}
